import { Component, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { GeneralLedgerService } from '../../services/general-ledger.service';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { GlPreviewModalComponent, GlPreviewModalData } from '../../shared/gl-preview-modal/gl-preview-modal.component';
import { GlImpactStatus } from '../../models/gl-preview.model';

export interface SystypeRow {
	typeid: number;
	typename: string;
	typeno: number;
	gltrans_row_count?: number;
	gltrans_doc_count?: number;
	gl_impact_status?: GlImpactStatus;
}

@Component({
	selector: 'app-gl-impact-view',
	templateUrl: './gl-impact-view.component.html',
	styleUrls: ['./gl-impact-view.component.scss'],
})
export class GlImpactViewComponent implements OnInit {
	color = 'blue';
	systypes: SystypeRow[] = [];
	displayedColumns: string[] = ['typeid', 'typename', 'typeno', 'glImpact', 'actions'];
	isLoading = false;
	loadError: string | null = null;

	constructor(
		private glService: GeneralLedgerService,
		private dialog: MatDialog,
		private globalSearchService: GlobalSearchService,
		private location: Location
	) {
		this.color = this.globalSearchService.getColor();
	}

	ngOnInit(): void {
		this.loadSystypes();
	}

	loadSystypes(): void {
		this.isLoading = true;
		this.loadError = null;
		this.glService.listSystypes().subscribe({
			next: (res: { success?: boolean; data?: SystypeRow[] }) => {
				this.isLoading = false;
				if (res?.success && Array.isArray(res.data)) {
					this.systypes = res.data;
				} else {
					this.loadError = 'Could not load transaction types.';
					this.systypes = [];
				}
			},
			error: () => {
				this.isLoading = false;
				this.loadError = 'Could not load transaction types.';
				this.systypes = [];
			},
		});
	}

	back(): void {
		this.location.back();
	}

	glImpactLabel(row: SystypeRow): string {
		const status = row.gl_impact_status;
		const rows = row.gltrans_row_count ?? 0;
		const docs = row.gltrans_doc_count ?? 0;
		if (status === 'posted') {
			const docPart = docs > 0 ? `, ${docs} doc${docs === 1 ? '' : 's'}` : '';
			return `Posted (${rows} row${rows === 1 ? '' : 's'}${docPart})`;
		}
		if (status === 'no_gl_expected') {
			return 'No GL (system)';
		}
		return 'No GL data yet';
	}

	glImpactClass(row: SystypeRow): string {
		const status = row.gl_impact_status;
		if (status === 'posted') {
			return 'gl-impact-badge gl-impact-badge--posted';
		}
		if (status === 'no_gl_expected') {
			return 'gl-impact-badge gl-impact-badge--system';
		}
		return 'gl-impact-badge gl-impact-badge--unknown';
	}

	openPreview(row: SystypeRow): void {
		const titleBase = `GL impact — ${row.typename} (type ${row.typeid})`;
		this.dialog.open(GlPreviewModalComponent, {
			width: '1160px',
			maxWidth: '98vw',
			panelClass: 'gl-preview-dialog-panel',
			data: {
				title: titleBase,
				allowResample: (row.gltrans_row_count ?? 0) > 0,
				getPreview: (excludeTypenos?: number[], sampleScope?: 'all' | 'current_year') =>
					this.glService.getHypotheticalGlImpact(row.typeid, {
						excludeTypenos: excludeTypenos ?? [],
						sampleScope: sampleScope ?? 'all',
					}),
			} as GlPreviewModalData,
		});
	}
}
